import { t } from "i18next";
import { match, P } from "ts-pattern";
import { z } from "zod";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  commandUnitSchema,
  forenameSchema,
  nonFutureDateSchema,
  OrganisationRoleSchema,
  shortTextSchema,
  surnameSchema,
  yesNoSchema,
} from "../../types/index.js";
import { tk } from "../../types/Translation.js";

/** Control Works numbers can be as short as three characters. */
const dispatchNumberSchema = z
  .string()
  .min(3, "fieldSchema.dispatchNumber.tooShort")
  .max(255);

export const formSpecification = new MultiPageFormBuilder()
  .withFieldAlwaysVisible({
    fieldName: "dispatchNumber", // create-1.0
    type: "textbox",
    ...new FieldBuilder().withSchema(dispatchNumberSchema).build(),
    label: tk("createIncident.dispatchNumber.label"),
    placeholder: tk("createIncident.dispatchNumber.placeholder"),
    requiredLabel: tk("fieldSchema.dispatchNumber.required"),
  })
  .withFieldAlwaysVisible({
    fieldName: "dispatchNumberCreated", // create-1.1
    type: "date",
    // The day the Control Works number was raised. Policing runs through the
    // night, so the day an officer is working has to be available to them even
    // in its first hour.
    // Incidents recorded before this question was asked hold no value here.
    ...new FieldBuilder().withSchema(nonFutureDateSchema).build(),
    label: tk("createIncident.dispatchNumberCreated"),
    requiredLabel: tk("createIncident.dispatchNumberCreatedRequired"),
    disableFuture: true,
  })
  .withFieldAlwaysVisible({
    fieldName: "commandUnit", // create-2.2
    type: "organisation",
    filterByRoles: [OrganisationRoleSchema.enum.PoliceCommandUnit],
    ...new FieldBuilder()
      .withSchema(commandUnitSchema)
      .captureValueLabel()
      .build(),
    label: tk("createIncident.commandUnit"),
  })
  .withFieldAlwaysVisible({
    fieldName: "geographicUnit", // create-2.3
    type: "organisationTypeahead",
    filterByRoles: [OrganisationRoleSchema.enum.PoliceGeographicUnit],
    ...new FieldBuilder()
      .withSchema(commandUnitSchema)
      .captureValueLabel()
      .build(),
    label: tk("createIncident.geographicUnit"),
  })
  .withFieldAlwaysVisible({
    fieldName: "userIsAttendingOfficer", // create-3.0
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    label: tk("createIncident.userIsAttendingOfficer"),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "createdOnBehalfOfAnotherOfficerWithShoulderNumber", // create-3.0.1
    type: "officerLookup",
    ...new FieldBuilder().withSchema(shortTextSchema).build(),
    label: tk(
      "createIncident.createdOnBehalfOfAnotherOfficerWithShoulderNumber",
    ),
    isVisible: ({ formValues }) =>
      match(formValues.userIsAttendingOfficer)
        .with({ value: "no", valueLabel: P._ }, () => true)
        .with(undefined, { value: "yes", valueLabel: P._ }, () => false)
        .exhaustive(),
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "createdOnBehalfOfAnotherOfficerBanner", // create-3.0.2
    type: "banner",
    bannerType: "info",
    body: tk("createIncident.createdOnBehalfOfAnotherOfficerBanner"),
    isVisible: ({ formValues }) =>
      match(formValues.userIsAttendingOfficer)
        .with({ value: "no", valueLabel: P._ }, () => true)
        .with(undefined, { value: "yes", valueLabel: P._ }, () => false)
        .exhaustive(),
  })
  .withFieldAlwaysVisible({
    fieldName: "personForename", // create-4.0
    type: "textbox",
    ...new FieldBuilder().withUnknowableSchema(forenameSchema).build(),
    label: tk("createIncident.personForename"),
  })
  .withFieldAlwaysVisible({
    fieldName: "personSurname", // create-4.1
    type: "textbox",
    ...new FieldBuilder().withUnknowableSchema(surnameSchema).build(),
    label: tk("createIncident.personSurname"),
  })
  .withPage([
    "dispatchNumber", // create-1.0
    "dispatchNumberCreated", // create-1.1
  ])
  .withPage([
    "commandUnit", // create-2.2
    "geographicUnit", // create-2.3
  ])
  .withPage([
    "userIsAttendingOfficer", // create-3.0
    "createdOnBehalfOfAnotherOfficerWithShoulderNumber", // create-3.0.1
    "createdOnBehalfOfAnotherOfficerBanner", // create-3.0.2
  ])
  .withPage([
    "personForename", // create-4.0
    "personSurname", // create-4.1
  ])
  .build({ t });

export type CreateIncidentWorkItemFormData = ReturnType<
  typeof formSpecification
>["formType"];
